import { useContext, useEffect, useState } from "react";

import { AppContext } from "../../../core/context/appContextProvider";
import { getCountryList, getStateList } from "../../../services/address.service";
import {
  autoCompleteAddress,
  getDetailsByIdPlace,
  getDetailsByZipCode,
} from "../../../services/autocomplete.service";
import { getLegalTypesList, getTypesList } from "../../../services/types.service";
import { investorEntityTypes, prefixOptions } from "../../../utils/constants/form.constants";
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync.hook";
import {
  InvestorDetail,
  MailingAddress,
} from "../../../utils/types/investor.type";
import { ListItem, SelectionOptionItem } from "../../../utils/types/listItems";

export const useAddressEffect = (
  setIsLoading: Function,
  investor: InvestorDetail,
  addressValues: MailingAddress,
  taxAddressValues: MailingAddress
) => {

  const { informationAlert } = useContext(AppContext);

  const [showMessageNoMatches, setShowMessageNoMatches] = useState<boolean>(false);

  const [countryList, setCountryList] = useState<ListItem[]>([]);
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [addressSuggestionsAnchorEl, setAddressSuggestionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [stateList, setStateList] = useState<ListItem[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>('');
  const [selectedState, setSelectedState] = useState<string | undefined>('');
  const [street1, setStreet1] = useState<string | undefined>('');
  const [city, setCity] = useState<string | undefined>('');
  const [zipCode, setZipCode] = useState<string | undefined>('');

  const [taxAddressSuggestions, setTaxAddressSuggestions] = useState<any[]>([]);
  const [taxAddressSuggestionsAnchorEl, setTaxAddressSuggestionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [taxStateList, setTaxStateList] = useState<ListItem[]>([]);
  const [selectedTaxCountry, setSelectedTaxCountry] = useState<string | undefined>('');
  const [selectedTaxState, setSelectedTaxState] = useState<string | undefined>('');
  const [taxStreet1, setTaxStreet1] = useState<string | undefined>('');
  const [taxCity, setTaxCity] = useState<string | undefined>('');
  const [taxZipCode, setTaxZipCode] = useState<string | undefined>('');
  const [hideTaxAddress, setHideTaxAddress] = useState<boolean>(false);

  const [phone, setPhone] = useState<string | undefined>('');

  useEffectAsync(async (isCanceled) => {
    try {
      setIsLoading(true);
      if (isCanceled()) return;
      const countriesResponse = await getCountryList();

      countriesResponse.some(
        (country, idx) =>
          country.id === "US" &&
          countriesResponse.unshift(countriesResponse.splice(idx, 1)[0])
      );

      setCountryList(countriesResponse);

    } catch (e) {
      setIsLoading(false);
      informationAlert("Error in getting country list", "error");
    }
  }, []);

  useEffect(() => {
    setPhone(investor.phone);
  }, [investor.phone]);

  useEffect(() => {
    setSelectedTaxCountry(taxAddressValues.country);
    setSelectedTaxState(taxAddressValues.state);
    setTaxStreet1(taxAddressValues.street1);
    setTaxCity(taxAddressValues.city);
    setTaxZipCode(taxAddressValues.postalCode);
  }, [taxAddressValues]);

  useEffect(() => {
    setSelectedCountry(addressValues.country);
    setStreet1(addressValues.street1);
    setCity(addressValues.city);
    setZipCode(addressValues.postalCode);
    setSelectedState(addressValues.state);
  }, [addressValues]);

  useEffect(() => {
    if (!hideTaxAddress) {
      resetTaxAddress();
    }
  }, [hideTaxAddress]);

  useEffectAsync(
    async (isCanceled) => {
      try {
        if (selectedCountry) {
          const statesResponse = await getStateList(selectedCountry);

          if (isCanceled()) return;

          setStateList(statesResponse);
        }
      } catch (e) {
        informationAlert("Error in getting states", "error");
      }
    }, [selectedCountry, selectedState]);

  useEffectAsync(
    async (isCanceled) => {
      try {
        if (selectedTaxCountry) {
          const statesResponse = await getStateList(selectedTaxCountry);

          if (isCanceled()) return;

          setTaxStateList(statesResponse);
        }
      } catch (e) {
        informationAlert("Error in getting states", "error");
      }
    }, [selectedTaxCountry, selectedTaxState]);

  const suggestedStateList = stateList.find((state) => state.id === selectedState)
    ? stateList
    : [...stateList, { id: selectedState, label: selectedState }];
  const suggestedCountryList = countryList.find((country) => country.id === selectedCountry)
    ? countryList
    : [...countryList, { id: selectedCountry, label: selectedCountry }];

  const suggestedTaxStateList = taxStateList.find((state) => state.id === selectedTaxState)
    ? taxStateList
    : [...taxStateList, { id: selectedTaxState, label: selectedTaxState }];
  const suggestedTaxCountryList = countryList.find((country) => country.id === selectedTaxCountry)
    ? countryList
    : [...countryList, { id: selectedTaxCountry, label: selectedTaxCountry }];

  useEffect(() => {
    if (
      investor.id !== '' &&
      investor.taxAddress &&
      investor.mailingAddress &&
      investor.mailingAddress.city === investor.taxAddress.city &&
      investor.mailingAddress.state === investor.taxAddress.state &&
      investor.mailingAddress.postalCode === investor.taxAddress.postalCode &&
      investor.mailingAddress.street1 === investor.taxAddress.street1 &&
      investor.mailingAddress.street2 === investor.taxAddress.street2
    ) {
      setHideTaxAddress(true);
    } else setHideTaxAddress(false);
  }, [investor.mailingAddress, investor.taxAddress]);

  const handleCountryChange = (event: any, field: string) => {
    switch (field) {
      case 'country':
        addressValues.country = event.target.value;
        setSelectedCountry(event.target.value);
        break;
      case 'taxCountry':
        taxAddressValues.country = event.target.value;
        setSelectedTaxCountry(event.target.value);
        break;
    }
  };

  const handleStateChange = (event: any, field: string) => {
    switch (field) {
      case 'state':
        addressValues.state = event.target.value;
        setSelectedState(event.target.value);
        break;
      case 'taxState':
        taxAddressValues.state = event.target.value;
        setSelectedTaxState(event.target.value);
        break;
    }
  };

  const handleCityChange = (event: any, field: string) => {
    switch (field) {
      case 'city':
        addressValues.city = event.target.value;
        setCity(event.target.value);
        break;
      case 'taxCity':
        taxAddressValues.city = event.target.value;
        setTaxCity(event.target.value);
        break;
    }
  };

  const handlePostalCodeChange = async (event: any, field: string) => {
    switch (field) {
      case 'zipCode':
        addressValues.postalCode = event.target.value;
        setZipCode(event.target.value);
        if (event.target.value.length >= 5) {
          addressValues.postalCode = event.target.value;

          const placeDetail = await getDetailsByZipCode(event.target.value);

          if (placeDetail.country || placeDetail.city) {
            addressValues.country = placeDetail.country;
            setSelectedCountry(placeDetail.country);
            addressValues.state = placeDetail.state;
            setSelectedState(placeDetail.state);
            addressValues.city = placeDetail.city;
            setCity(placeDetail.city);
          } else {
            resetAddress();
            resetTaxAddress();
          }
        } else {
          resetAddress();
          resetTaxAddress();
        }
        break;
      case 'taxZipCode':
        taxAddressValues.postalCode = event.target.value;
        setTaxZipCode(event.target.value);
        break;
    }
  };

  const onTaxAddressCheck = (event: any) => {
    if (event.target.checked) {
      copyTaxAddress();
    } else {
      resetTaxAddress();
    }

    setHideTaxAddress(event.target.checked);
  };

  const handleCloseArrowPopover = (addressType: string) => {
    switch (addressType) {
      case 'mailing':
        setAddressSuggestionsAnchorEl(null);
        break;
      case 'tax':
        setTaxAddressSuggestionsAnchorEl(null);
        break;
    }
  };

  const handleOnAddressChange = async ({ target, currentTarget }: any, addressType: string) => {
    const { value } = target;

    const result: any[] = await autoCompleteAddress(value);

    switch (addressType) {
      case 'mailing': {
        value ? setAddressSuggestionsAnchorEl(currentTarget) : setAddressSuggestionsAnchorEl(null);

        setStreet1(value);
        if (result) {
          const found = result.filter((item) => item.description.toLowerCase() === value.toLowerCase());

          found.length === 0 ? setShowMessageNoMatches(true) : setShowMessageNoMatches(false);
          setAddressSuggestions(result);
        } else {
          setAddressSuggestionsAnchorEl(null);
        }
        break;
      }
      case 'tax': {
        value ? setTaxAddressSuggestionsAnchorEl(currentTarget) : setTaxAddressSuggestionsAnchorEl(null);

        setTaxStreet1(value);

        if (result) {
          const found = result.filter((item) => item.description.toLowerCase() === value.toLowerCase());

          found.length === 0 ? setShowMessageNoMatches(true) : setShowMessageNoMatches(false);
          setTaxAddressSuggestions(result);
        } else {
          setTaxAddressSuggestionsAnchorEl(null);
        }
        break;
      }
    }
  };

  const handleOnPhoneChange = (e: any) => {
    const newValue = e.target.value;

    const allowedChars = new RegExp(/^[0-9+() .-]*$/);

    if (newValue.match(allowedChars)) {
      setPhone(newValue);
      investor.phone = newValue;
    }
  };

  const handleSelectClick = async (placeId: string, routeName: string, addressType: string) => {
    const resp = await getDetailsByIdPlace(placeId);

    if (resp) {
      switch (addressType) {
        case 'mailing':
          addressValues.street1 = routeName;
          addressValues.country = resp.country;
          addressValues.state = resp.state;
          addressValues.postalCode = resp.zipCode;
          addressValues.city = resp.city;
          setSelectedCountry(resp.country);
          setStreet1(routeName);
          setSelectedState(resp.state);
          setZipCode(resp.zipCode);
          setCity(resp.city);
          setAddressSuggestionsAnchorEl(null);
          break;
        case 'tax':
          taxAddressValues.street1 = routeName;
          taxAddressValues.country = resp.country;
          taxAddressValues.state = resp.state;
          taxAddressValues.postalCode = resp.zipCode;
          taxAddressValues.city = resp.city;
          setTaxStreet1(routeName);
          setSelectedTaxCountry(resp.country);
          setSelectedTaxState(resp.state);
          setTaxZipCode(resp.zipCode);
          setTaxCity(resp.city);
          setTaxAddressSuggestionsAnchorEl(null);
          break;
      }
    }
  };

  const copyTaxAddress = () => {
    investor.taxAddress!.id = addressValues.id;
    investor.taxAddress!.country = addressValues.country;
    investor.taxAddress!.state = addressValues.state;
    investor.taxAddress!.city = addressValues.city;
    investor.taxAddress!.postalCode = addressValues.postalCode;
    investor.taxAddress!.street1 = addressValues.street1;
    investor.taxAddress!.street2 = addressValues.street2;
  };

  const resetAddress = () => {
    addressValues.id = '';
    addressValues.country = '';
    addressValues.state = '';
    addressValues.city = '';
    addressValues.postalCode = '';
    addressValues.street1 = '';
    addressValues.street2 = '';
    setSelectedCountry('');
    setSelectedState('');
    setCity('');
    setZipCode('');
    setStreet1('');
  };

  const resetTaxAddress = () => {
    taxAddressValues.id = '';
    taxAddressValues.country = '';
    taxAddressValues.state = '';
    taxAddressValues.city = '';
    taxAddressValues.postalCode = '';
    taxAddressValues.street1 = '';
    taxAddressValues.street2 = '';
    setSelectedTaxCountry('');
    setSelectedTaxState('');
    setTaxCity('');
    setTaxZipCode('');
    setTaxStreet1('');
  };

  return {
    selectedCountry,
    selectedState,
    street1,
    city,
    zipCode,
    suggestedStateList,
    suggestedCountryList,
    selectedTaxCountry,
    selectedTaxState,
    taxStreet1,
    taxCity,
    taxZipCode,
    suggestedTaxStateList,
    suggestedTaxCountryList,
    showMessageNoMatches,
    addressSuggestions,
    addressSuggestionsAnchorEl,
    taxAddressSuggestions,
    taxAddressSuggestionsAnchorEl,
    hideTaxAddress,
    handleCountryChange,
    phone,
    setPhone,
    handleCityChange,
    handleStateChange,
    handlePostalCodeChange,
    handleOnAddressChange,
    handleCloseArrowPopover,
    handleSelectClick,
    handleOnPhoneChange,
    onTaxAddressCheck
  };
};

export const useInvestorTypesEffect = (
  setIsLoading: Function,
  investor: InvestorDetail,
  setUniqueNameError: Function,
  setRequiredFieldUpdated: any
) => {
  const [selectedInvestorType, setSelectedInvestorType] = useState<string>('');
  const [selectedLegalType, setSelectedLegalType] = useState<string>('');
  const [investorName, setInvestorName] = useState<string>('');
  const [investorPrefix, setInvestorPrefix] = useState<string>('');
  const [isEmptyNameError, setEmptyNameError] = useState<any>(false);

  const [investorTypeList, setInvestorTypesList] = useState<ListItem[]>([]);
  const [legalTypesList, setLegalTypesList] = useState<ListItem[]>([]);
  const [entityTypeOptions, setEntityTypeOptions] = useState<SelectionOptionItem[]>(investorEntityTypes);

  const {
    informationAlert
  } = useContext(AppContext);

  useEffectAsync(async (isCanceled) => {
    try {
      setIsLoading(true);

      if (isCanceled()) return;
      const typesResponse = await getTypesList();

      if (isCanceled()) return;
      const legalTypesResponse = await getLegalTypesList();

      if (isCanceled()) return;

      setLegalTypesList(legalTypesResponse);
      setInvestorTypesList(typesResponse);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      informationAlert("Error in getting investor details", "error");
    }
  }, []);

  useEffect(() => {
    setInvestorName(investor.name || '');
    if (investor && investor.investorType) {
      setSelectedInvestorType(investor.investorType.id);
    } else {
      setSelectedInvestorType(investorTypeList?.[0]?.id);
      investor.investorType = investorTypeList[0];
    }
    if (investor && investor.legalType) {
      setSelectedLegalType(investor.legalType.id);
    }

    if (investor && investor.pre) {
      setInvestorPrefix(investor.pre);
    }
  }, [investor]);

  const handleTypeChange = (event: any) => {
    setSelectedInvestorType(event.target.value);
    const investorTypeSelected = (investor.investorType =
      investorTypeList.find((type) => type.id === event.target.value));

    if (investorTypeSelected)
      investor.investorType = investorTypeSelected;
  };

  const handleLegalTypeChange = (event: any) => {
    setSelectedLegalType(event.target.value);
    const legalTypeSelected = (investor.legalType =
      legalTypesList.find((type) => type.id === event.target.value));

    if (legalTypeSelected) investor.legalType = legalTypeSelected;
  };

  const handleOnNameChange = (event: any) => {
    investor.name = event.target.value;
    setInvestorName(event.target.value);
    if (!event.target.value) {
      setEmptyNameError(true);
      setRequiredFieldUpdated(true);
      return;
    }
    setUniqueNameError(false);
    setEmptyNameError(false);

    setRequiredFieldUpdated(true);
  };

  const handlePrefixChange = (event: any) => {
    investor.pre = event.target.value;
    const prefix = prefixOptions.find((pre) => pre.id === event.target.value);

    setInvestorPrefix(prefix?.id || '');
    investor.pre = prefix?.id || '';
  };

  return {
    selectedInvestorType,
    selectedLegalType,
    investorName,
    investorPrefix,
    prefixOptions,
    entityTypeOptions,
    investorTypeList,
    legalTypesList,
    handleOnNameChange,
    handlePrefixChange,
    handleTypeChange,
    handleLegalTypeChange,
    isEmptyNameError
  };
};
